import * as Sentry from "@sentry/react-router";
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  isRouteErrorResponse,
  useRouteError,
} from "react-router";
import tailwindStylesheet from "./tailwind.css?url";
import type { LinksFunction, MetaFunction } from "react-router";

import NotFound from "./components/error-boundary/NotFound";
import SomethingWentWrong from "./components/error-boundary/SomethingWentWrong";
import { getMaxUploadSizeMB, getImageQuality } from "./utils/environment";

export const links: LinksFunction = () => [
  { rel: "stylesheet", href: tailwindStylesheet },
  { rel: "stylesheet", href: "https://rsms.me/inter/inter.css" },
  { rel: "icon", type: "image/x-icon", href: "/favicon.ico?v=2" },
];

export const meta: MetaFunction = () => {
  return [{ title: "Park Road Concierge" }];
};

export const handle = { id: "root" };

export async function loader() {
  console.log("MAX_UPLOAD_SIZE_MB", getMaxUploadSizeMB());
  return {
    ENV: {
      MAX_UPLOAD_SIZE_MB: getMaxUploadSizeMB(),
      IMAGE_QUALITY: getImageQuality(),
    },
  };
}

// React Router sanitizes server-thrown Errors to this exact shape before
// serializing them to the client, so it identifies an error handleError has
// already reported. See sanitizeError in react-router's server runtime.
const isServerReportedError = (error: unknown) =>
  error instanceof Error && error.message === "Unexpected Server Error";

export const ErrorBoundary = () => {
  const error = useRouteError();

  // Unmatched URLs and loaders that throw a 404 are expected traffic, not
  // failures - render the 404 page and keep them out of Sentry.
  if (isRouteErrorResponse(error) && error.status === 404) {
    return <NotFound />;
  }

  // handleError in entry.server.tsx reports anything that fails on the server,
  // with the real stack, so capturing those here too would file a second event
  // for one failure. React Router hands the browser a bare "Unexpected Server
  // Error" in place of the real one, and routes throw Responses deliberately -
  // skipping both shapes leaves errors that originated in the browser, which is
  // exactly what handleError never sees.
  if (
    process.env.NODE_ENV === "production" &&
    typeof document !== "undefined" &&
    !isRouteErrorResponse(error) &&
    !isServerReportedError(error)
  ) {
    Sentry.captureException(error);
  }

  if (process.env.NODE_ENV !== "production") {
    console.error("Error Boundary caught an error:", error);
    if (error instanceof Error) {
      console.error("Error message:", error.message);
      console.error("Error stack:", error.stack);
    }
  }
  
  return <SomethingWentWrong />;
};

// The document shell lives in Layout so that it also wraps whatever the
// ErrorBoundary renders. Without it, error pages ship as a bare fragment with
// no <head>, and therefore no stylesheet.
export function Layout({ children }: { children: React.ReactNode }) {
  return (
    <html className="h-full bg-white" lang="en">
      <head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <Meta />
        <Links />
      </head>
      <body className="h-full">
        {children}
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  );
}

export default function App() {
  return <Outlet />;
}
